// §4.17 MERGE — the preview's parts (Module 09, P3-E2 PR E2-2b; spec 09 §3.13
// and §4.17, D-324 rulings 4 to 11). Each part paints what the preview served
// and reports the admin's choice upward; none of them holds state of its own,
// so the panel above them (`merge-panel.tsx`) is the one place the body the
// merge sends is assembled, from `merge-model.ts`.
//
// REDACTION IS OMISSION (§7). The legal name follows `LegalNameReach` and the
// credit limit the customer card's own predicate, and the preview OMITS either
// key for a reader they are not served to. Their rows still show, with no
// value, because the choice still matters to the merge: the radio for the
// limit moves with the currency whether or not the reader sees the figure
// (§4.17). Nothing is printed in the omitted value's place, the register's
// rule for a value the reader may not see (an em dash would read as "this
// customer has none").
//
// THE TABLES ARE THE ONE TABLE PRIMITIVE. Both the field grid and the counts
// are `DataTable`s (design-system gate 3c forbids a raw `<table>` under
// areas/), and the tax id's 400 is the field grid's expansion row under the
// tax id, so the refusal sits on the row it names (§4.17: "A 400 on the tax id
// shows on the tax id's row and leaves every choice as the admin set it").

import { useTranslation } from "react-i18next";
import { CheckboxField } from "../../../../../components/field";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import { formatMinor } from "../../lib/money";
import type {
  MergeFieldChoices,
  MergePreview,
  MergeProfile,
  MergeSide,
} from "../../hooks/merge-queries";
import {
  MERGE_FIELDS,
  addressLine,
  fieldLinked,
  fieldLocked,
  kindRows,
  type ChoiceRules,
  type MergeField,
  type MergeKindRow,
} from "./merge-model";

type T = ReturnType<typeof useTranslation>["t"];

/** One profile value as the reader reads it, or `undefined` when the preview
 * omitted it (§7) and nothing may be printed. */
export function fieldValue(
  profile: MergeProfile,
  field: MergeField,
  t: T,
  language: string,
): string | undefined {
  switch (field) {
    case "legal_name":
      return profile.legal_name;
    case "display_name":
      return profile.display_name;
    case "tax_id":
      return profile.tax_id ?? t("sales.merge.notRecorded");
    case "country":
      return profile.country;
    case "tier":
      return t(`sales.tier.${profile.tier}`);
    case "default_currency":
      return profile.default_currency;
    case "credit_limit_minor":
      if (profile.credit_limit_minor === undefined) return undefined;
      return profile.credit_limit_minor === 0
        ? t("sales.customers.prepayOnly")
        : formatMinor(profile.credit_limit_minor, profile.default_currency, language);
    case "credit_terms_days":
      return t("sales.c360.termsDays", { count: profile.credit_terms_days });
    case "credit_hold":
      return profile.credit_hold ? t("sales.merge.holdOn") : t("sales.merge.holdOff");
    case "billing_address":
    case "shipping_address": {
      const line = addressLine(profile[field]);
      return line.length > 0 ? line : t("sales.merge.notRecorded");
    }
    case "preferred_channel":
      return profile.preferred_channel === "none"
        ? t("sales.merge.channelNone")
        : t(`sales.channel.${profile.preferred_channel}`);
    case "preferred_locale":
      return t(`sales.c360.localeName.${profile.preferred_locale}`);
  }
}

interface FieldRow {
  readonly field: MergeField;
}

/** The two columns of values with a radio each (§4.17). */
export function MergeFieldChoicesTable({
  preview,
  choices,
  rules,
  taxIdRefused,
  onChoose,
}: {
  preview: MergePreview;
  choices: MergeFieldChoices;
  rules: ChoiceRules;
  taxIdRefused: boolean;
  onChoose: (field: MergeField, side: MergeSide) => void;
}) {
  const { t, i18n } = useTranslation();
  const label = (field: MergeField) => t(`sales.merge.field.${field}`);
  const choiceCell = (row: FieldRow, side: MergeSide) => {
    const profile = side === "winner" ? preview.winner : preview.loser;
    const locked = side === "loser" && fieldLocked(row.field, rules);
    const value = fieldValue(profile, row.field, t, i18n.language);
    return (
      <label className={locked ? "sales-merge-choice is-locked" : "sales-merge-choice"}>
        <input
          type="radio"
          name={`sales-merge-${row.field}`}
          value={side}
          checked={choices[row.field] === side}
          disabled={locked}
          aria-label={t("sales.merge.keepFrom", {
            field: label(row.field),
            name: profile.display_name,
          })}
          data-testid="sales-merge-choice"
          data-field={row.field}
          data-side={side}
          onChange={() => {
            onChoose(row.field, side);
          }}
        />
        <span data-testid="sales-merge-value" data-field={row.field} data-side={side}>
          {value}
        </span>
      </label>
    );
  };
  const columns: DataTableColumn<FieldRow>[] = [
    {
      key: "field",
      header: t("sales.merge.colField"),
      id: true,
      render: (row) => label(row.field),
    },
    {
      key: "winner",
      header: `${preview.winner.display_name} · ${t("sales.merge.stays")}`,
      render: (row) => choiceCell(row, "winner"),
    },
    {
      key: "loser",
      header: `${preview.loser.display_name} · ${t("sales.merge.mergedIn")}`,
      render: (row) => choiceCell(row, "loser"),
    },
  ];
  return (
    <section className="sales-merge-section" data-testid="sales-merge-fields">
      <h3>{t("sales.merge.fieldsTitle")}</h3>
      <DataTable
        columns={columns}
        rows={MERGE_FIELDS.map((field) => ({ field }))}
        rowKey={(row) => row.field}
        empty={null}
        caption={t("sales.merge.fieldsTitle")}
        rowTestId="sales-merge-field-row"
        rowProps={(row) => ({
          "data-field": row.field,
          "data-linked": fieldLinked(row.field, rules) ? "true" : "false",
          "data-locked": fieldLocked(row.field, rules) ? "true" : "false",
        })}
        expansion={(row) =>
          row.field === "tax_id" && taxIdRefused ? (
            <p className="sales-error-note" role="alert" data-testid="sales-merge-taxid-refused">
              {t("sales.merge.taxIdRefused")}
            </p>
          ) : undefined
        }
      />
      {rules.currenciesDiffer ? (
        <p className="sales-hint" data-testid="sales-merge-currency-linked">
          {t("sales.merge.currencyLinked")}
        </p>
      ) : null}
      {fieldLocked("tier", rules) ? (
        <p className="sales-hint" data-testid="sales-merge-tier-locked">
          {t("sales.merge.tierLocked")}
        </p>
      ) : null}
    </section>
  );
}

/** A table per kind of row: how many move, how many stay and why (§4.17). */
export function MergeKindCounts({
  preview,
  tierChanged,
}: {
  preview: MergePreview;
  /** The admin keeps the merged-in customer's differing tier, so the
   * distributor-product count read against the current tier may move. */
  tierChanged: boolean;
}) {
  const { t } = useTranslation();
  const count = (value: number | undefined) => value ?? null;
  const columns: DataTableColumn<MergeKindRow>[] = [
    {
      key: "kind",
      header: t("sales.merge.colKind"),
      id: true,
      render: (row) => t(`sales.merge.kind.${row.kind}`),
    },
    {
      key: "moves",
      header: t("sales.merge.colMoves"),
      numeric: true,
      render: (row) => count(row.moves),
    },
    {
      key: "stays",
      header: t("sales.merge.colStays"),
      numeric: true,
      render: (row) => count(row.stays),
    },
    {
      key: "why",
      header: t("sales.merge.colWhy"),
      render: (row) =>
        row.stays !== undefined && row.stays > 0 ? t(`sales.merge.stayWhy.${row.kind}`) : null,
    },
  ];
  return (
    <section className="sales-merge-section" data-testid="sales-merge-counts">
      <h3>{t("sales.merge.rowsTitle")}</h3>
      <DataTable
        columns={columns}
        rows={kindRows(preview)}
        rowKey={(row) => row.kind}
        empty={null}
        caption={t("sales.merge.rowsTitle")}
        rowTestId="sales-merge-kind-row"
        rowProps={(row) => ({
          "data-kind": row.kind,
          "data-moves": row.moves ?? "",
          "data-stays": row.stays ?? "",
        })}
      />
      {tierChanged ? (
        <p className="sales-hint" data-testid="sales-merge-tier-recount">
          {t("sales.merge.tierRecount")}
        </p>
      ) : null}
    </section>
  );
}

/** The people who would gain access, each with a tick (§3.13, §4.17). */
export function MergeAccessList({
  preview,
  cleared,
  disabled,
  onToggle,
}: {
  preview: MergePreview;
  cleared: ReadonlySet<string>;
  disabled: boolean;
  onToggle: (userId: string, keep: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="sales-merge-section" data-testid="sales-merge-access">
      <h3>{t("sales.merge.accessTitle")}</h3>
      {preview.gains_access.length === 0 ? (
        <p className="sales-hint" data-testid="sales-merge-access-none">
          {t("sales.merge.accessNone")}
        </p>
      ) : (
        <>
          <p className="sales-hint">{t("sales.merge.accessBody")}</p>
          <ul className="sales-merge-list">
            {preview.gains_access.map((grant) => (
              <li
                key={grant.user_id}
                data-testid="sales-merge-access-row"
                data-user={grant.user_id}
                data-kept={cleared.has(grant.user_id) ? "false" : "true"}
              >
                <CheckboxField
                  label={grant.name}
                  checked={!cleared.has(grant.user_id)}
                  disabled={disabled}
                  data-testid="sales-merge-access-keep"
                  onChange={(checked) => {
                    onToggle(grant.user_id, checked);
                  }}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** The commercial attachments whose audience changes, or the one line that
 * counts them for a reader without `sales.read_value` (§7 narrowing 19). */
export function MergeCommercialFiles({ preview }: { preview: MergePreview }) {
  const { t } = useTranslation();
  const listed = preview.commercial_audience_changes;
  return (
    <section className="sales-merge-section" data-testid="sales-merge-files">
      <h3>{t("sales.merge.filesTitle")}</h3>
      {listed === undefined ? (
        <p className="sales-hint" data-testid="sales-merge-files-withheld">
          {t("sales.merge.filesWithheld", {
            count: preview.commercial_audience_withheld_count ?? 0,
          })}
        </p>
      ) : listed.length === 0 ? (
        <p className="sales-hint" data-testid="sales-merge-files-none">
          {t("sales.merge.filesNone")}
        </p>
      ) : (
        <>
          <p className="sales-hint">{t("sales.merge.filesBody")}</p>
          <ul className="sales-merge-list">
            {listed.map((file) => (
              <li key={file.id} data-testid="sales-merge-file" data-file={file.id}>
                {file.title ?? t("sales.merge.filesUntitled")}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** The blockers, read plainly (§4.17). Renders nothing when none stands. */
export function MergeBlockers({ preview }: { preview: MergePreview }) {
  const { t } = useTranslation();
  if (preview.blockers.length === 0) return null;
  return (
    <section className="sales-merge-section" data-testid="sales-merge-blockers" role="alert">
      <h3>{t("sales.merge.blockersTitle")}</h3>
      <ul className="sales-merge-blockers">
        {preview.blockers.map((code) => (
          <li key={code} data-testid="sales-merge-blocker" data-code={code}>
            {t(`sales.merge.blocker.${code}`)}
            {/* Each clashing reference with both orders, so the admin knows
                which one to cancel (D-359). */}
            {code === "MERGE_IMPORT_ORDER_REF_CONFLICT" &&
            (preview.import_order_ref_conflicts ?? []).length > 0 ? (
              <ul data-testid="sales-merge-import-conflicts">
                {(preview.import_order_ref_conflicts ?? []).map((clash) => (
                  <li key={clash.order_ref} data-testid="sales-merge-import-conflict">
                    {t("sales.merge.importConflict", {
                      ref: clash.order_ref,
                      loser: clash.loser_order_no,
                      winner: clash.winner_order_no,
                    })}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="sales-hint">{t("sales.merge.blockersHint")}</p>
    </section>
  );
}
